import { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, ShieldCheck, Settings, Users, Globe, ArrowRight } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';
import axios from 'axios';

const CHEF_IMG = 'https://images.pexels.com/photos/34021716/pexels-photo-34021716.jpeg';
const CATERING_IMG = 'https://images.unsplash.com/photo-1719786625035-71f46082e385?crop=entropy&cs=srgb&fm=jpg';
const HERO_IMG = '/images/products-banner.jpg';
const SPICE_DARK = 'https://images.pexels.com/photos/37568646/pexels-photo-37568646.jpeg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, delay },
});

const clientSegments = [
  {
    title: 'Restaurants & Cafes',
    desc: 'From fine-dining to local eateries, we supply consistent, premium-grade spices in bulk. Our blends are crafted to maintain the same taste profile across thousands of servings, ensuring your signature dishes never lose their character.',
    bullets: ['Consistent quality across batches', 'Competitive bulk pricing', 'FSSAI certified products'],
    img: CHEF_IMG,
    bgClass: 'bg-[#1B3A6B]',
    textClass: 'text-[#F9F3E8]',
    descClass: 'text-[#F9F3E8]/70',
  },
  {
    title: 'Cloud Kitchens',
    desc: 'High-volume delivery kitchens need precision and consistency. Our portion-ready masala packs and pre-measured blends are designed for rapid kitchen operations where speed meets quality.',
    bullets: ['Portion-perfect packaging', 'High-volume supply', 'Delivery kitchen optimized'],
    img: CATERING_IMG,
    bgClass: 'bg-[#F9F3E8]',
    textClass: 'text-[#1B3A6B]',
    descClass: 'text-[#5C3D11]',
  },
  {
    title: 'Catering Companies',
    desc: 'From wedding feasts to corporate events, our custom masala solutions scale effortlessly. We offer institutional-grade packaging and event-scale quantities with advance planning support.',
    bullets: ['Custom masala blends', 'Event-scale quantities', 'Institutional supply chain'],
    img: HERO_IMG,
    bgClass: 'bg-[#1B3A6B]',
    textClass: 'text-[#F9F3E8]',
    descClass: 'text-[#F9F3E8]/70',
  },
];

const b2bBenefits = [
  { icon: Package, title: 'Bulk Order Discounts', desc: 'Competitive pricing for large orders' },
  { icon: Truck, title: 'Consistent Supply Chain', desc: 'Reliable delivery schedules you can count on' },
  { icon: ShieldCheck, title: 'FSSAI Compliant', desc: 'All products meet food safety standards' },
  { icon: Settings, title: 'Custom Packaging', desc: 'Tailored packaging for your business needs' },
  { icon: Users, title: 'Dedicated Support', desc: 'Personal account manager for your business' },
  { icon: Globe, title: 'Pan-India Delivery', desc: 'Supply across all major cities in India' },
];

export default function ClientsPage() {
  const [formData, setFormData] = useState({ full_name: '', company_name: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.company_name) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, { ...formData, source_page: 'b2b' });
      setSubmitted(true);
      setFormData({ full_name: '', company_name: '', city: '', message: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={TESTIDS.CLIENTS_PAGE}>
      {/* ===== PAGE HERO ===== */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 parallax-bg" style={{ backgroundImage: `url(${SPICE_DARK})` }} />
        <div className="absolute inset-0 bg-[#1A1A1A]/75" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">B2B Partners</p>
            <h1 className="font-playfair text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Trusted by Professional<br />Kitchens Across India
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ===== CLIENT SEGMENT CARDS ===== */}
      {clientSegments.map((seg, i) => (
        <section key={i} className={seg.bgClass}>
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 md:grid-cols-2 ${i % 2 === 1 ? '' : ''}`}>
              <motion.div
                {...fadeUp(0.1)}
                className={`p-8 md:p-16 flex flex-col justify-center ${i % 2 === 1 ? 'md:order-2' : ''}`}
              >
                <h2 className={`font-playfair text-2xl md:text-4xl mb-4 ${seg.textClass}`}>{seg.title}</h2>
                <p className={`font-lato text-base leading-relaxed mb-6 ${seg.descClass}`}>{seg.desc}</p>
                <ul className="space-y-2 mb-6">
                  {seg.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9922C]" />
                      <span className={`font-lato text-sm ${seg.descClass}`}>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div
                {...fadeUp(0.2)}
                className={`${i % 2 === 1 ? 'md:order-1' : ''}`}
              >
                <img src={seg.img} alt={seg.title} className="w-full h-64 md:h-full object-cover" loading="lazy" />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* ===== WHY B2B ===== */}
      <section className="bg-[#F9F3E8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Why Partner With Us</p>
            <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-5xl">Why B2B with Anuruchi?</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {b2bBenefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-4 p-6 bg-white border border-[#C9922C]/15 hover:border-[#C9922C]/50 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full border border-[#C9922C]/30 flex items-center justify-center">
                  <b.icon size={20} className="text-[#C9922C]" />
                </div>
                <div>
                  <h4 className="font-playfair text-[#1B3A6B] text-lg mb-1">{b.title}</h4>
                  <p className="font-lato text-[#5C3D11] text-sm">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ENQUIRY FORM ===== */}
      <section className="bg-[#1B3A6B] py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <h2 className="font-playfair text-[#F9F3E8] text-3xl md:text-4xl mb-3">Become a Supply Partner</h2>
            <p className="font-lato text-[#F9F3E8]/60 text-base">Fill in your details and we'll reach out within 24 hours</p>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              data-testid="b2b-form-success"
              className="text-center py-12"
            >
              <p className="font-playfair text-[#C9922C] text-2xl mb-2">Thank You!</p>
              <p className="font-lato text-[#F9F3E8]/70">Your enquiry has been submitted. We'll contact you soon.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                data-testid={TESTIDS.B2B_FORM_NAME}
                type="text" placeholder="Your Name *" required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="brand-input !text-[#F9F3E8] !border-[#F9F3E8]/20 focus:!border-[#C9922C] placeholder:!text-[#F9F3E8]/40"
              />
              <input
                data-testid={TESTIDS.B2B_FORM_COMPANY}
                type="text" placeholder="Company Name *" required
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="brand-input !text-[#F9F3E8] !border-[#F9F3E8]/20 focus:!border-[#C9922C] placeholder:!text-[#F9F3E8]/40"
              />
              <input
                data-testid={TESTIDS.B2B_FORM_CITY}
                type="text" placeholder="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="brand-input !text-[#F9F3E8] !border-[#F9F3E8]/20 focus:!border-[#C9922C] placeholder:!text-[#F9F3E8]/40 md:col-span-2"
              />
              <textarea
                data-testid={TESTIDS.B2B_FORM_REQUIREMENT}
                placeholder="Your Requirement"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="brand-textarea !text-[#F9F3E8] !border-[#F9F3E8]/20 focus:!border-[#C9922C] placeholder:!text-[#F9F3E8]/40 md:col-span-2"
                rows={4}
              />
              <div className="md:col-span-2">
                <button
                  data-testid={TESTIDS.B2B_FORM_SUBMIT}
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Enquiry'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
